import type { FlagSummary } from '@zuruflex/api-client';
import type { FlagJson } from '@zuruflex/types';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@zuruflex/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FlagValueEditorProps {
  flag: Pick<FlagSummary, 'type' | 'options'>;
  value: FlagJson;
  onChange: (value: FlagJson) => void;
  /**
   * Told when the text in the box stops (or starts) being a value at all —
   * half-typed JSON, an empty number. The draft keeps its last good value
   * meanwhile, so the page needs this to know Save must wait.
   */
  onInvalid?: (error: string | null) => void;
  disabled?: boolean;
  id?: string;
}

/**
 * The one control that fits the flag's declared type.
 *
 * The JSON and number boxes keep their own text: re-rendering the parsed
 * value on every keystroke would eat the trailing `{` or `.` somebody is in
 * the middle of typing. Callers remount it (with a `key`) to load a value
 * from outside, such as after Discard.
 */
export function FlagValueEditor({
  flag,
  value,
  onChange,
  onInvalid,
  disabled,
  id,
}: FlagValueEditorProps) {
  const { t } = useTranslation('admin');

  if (flag.type === 'boolean') {
    return (
      <label className="inline-flex items-center gap-3 text-sm">
        <Switch
          id={id}
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked)}
        />
        <span className="font-mono">{value === true ? 'true' : 'false'}</span>
      </label>
    );
  }

  if (flag.type === 'string' && flag.options?.length) {
    return (
      <Select
        value={typeof value === 'string' ? value : ''}
        onValueChange={(next) => onChange(next)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="h-10">
          <SelectValue placeholder={t('flags.value.choose')} />
        </SelectTrigger>
        <SelectContent>
          {flag.options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (flag.type === 'string') {
    return (
      <Input
        id={id}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (flag.type === 'number') {
    return <NumberEditor {...{ id, value, onChange, onInvalid, disabled }} />;
  }

  return <JsonEditor {...{ id, value, onChange, onInvalid, disabled }} />;
}

type TextEditorProps = Omit<FlagValueEditorProps, 'flag'>;

function NumberEditor({ id, value, onChange, onInvalid, disabled }: TextEditorProps) {
  const { t } = useTranslation('admin');
  const [text, setText] = useState(() => (typeof value === 'number' ? String(value) : ''));
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-1">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={text}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          const raw = event.target.value;
          setText(raw);
          const parsed = Number(raw);
          const problem = raw.trim() === '' || !Number.isFinite(parsed) ? t('flags.value.notNumber') : null;
          setError(problem);
          onInvalid?.(problem);
          if (!problem) onChange(parsed);
        }}
      />
      {error ? <p className="text-xs font-medium text-danger">{error}</p> : null}
    </div>
  );
}

function JsonEditor({ id, value, onChange, onInvalid, disabled }: TextEditorProps) {
  const { t } = useTranslation('admin');
  const [text, setText] = useState(() => JSON.stringify(value, null, 2) ?? 'null');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-1">
      <Textarea
        id={id}
        rows={Math.min(14, Math.max(4, text.split('\n').length + 1))}
        spellCheck={false}
        className="font-mono text-xs"
        value={text}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          const raw = event.target.value;
          setText(raw);
          try {
            const parsed = JSON.parse(raw) as FlagJson;
            setError(null);
            onInvalid?.(null);
            onChange(parsed);
          } catch (cause) {
            // The parser's own message carries the position, which is
            // the part that makes a long blob fixable.
            const problem = t('flags.value.badJson', {
              reason: cause instanceof Error ? cause.message : '',
            });
            setError(problem);
            onInvalid?.(problem);
          }
        }}
      />
      {error ? (
        <p className="text-xs font-medium text-danger">{error}</p>
      ) : (
        <p className="text-xs text-text-muted">{t('flags.value.jsonHint')}</p>
      )}
    </div>
  );
}
